import { useEffect } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { MatchPublic } from '@mondy/shared';
import { liveMinuteLabel, matchPausedLabel } from '@mondy/shared';
import { ApiError } from '@mondy/shared/api-client';
import { api } from '@/api';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMyGroups } from '@/hooks/useMyGroups';
import { fallbackScopeFor, useActiveGroup, useSetActiveGroup } from '@/state/activeGroup';
import { cn } from '@/lib/cn';
import type { StandingsRow } from '@/components/MatchStandings';
import { Spinner } from '@/components/Spinner';
import { TeamFlag } from '@/components/TeamFlag';
import { CrownedFlag } from '@/components/CrownedFlag';
import { TierBadge } from '@/components/TierBadge';

interface StandingsResponse {
  standings: StandingsRow[];
  total: number;
}

interface LiveMatchesResponse {
  matches: MatchPublic[];
}

export function StandingsSidebar() {
  const me = useCurrentUser();
  const groupsQuery = useMyGroups();
  const activeGroupId = useActiveGroup();
  const setActiveGroupId = useSetActiveGroup();

  const groups = groupsQuery.data?.groups ?? [];
  const isAdmin = me?.role === 'admin';

  // The sidebar renders on every page, so it has to migrate stale persisted
  // scopes itself — without this a non-admin who already had `'global'` in
  // localStorage would 403 indefinitely on every page until they visited
  // /standings. Mirrors the reconcile in <StandingsPage /> for consistency.
  useEffect(() => {
    if (me === undefined) return;
    if (!groupsQuery.data) return;
    const fallback = fallbackScopeFor(!!isAdmin, groups.map((g) => g.id));
    if (activeGroupId === 'global') {
      if (!isAdmin) setActiveGroupId(fallback);
      return;
    }
    if (activeGroupId === 'self') {
      if (groups.length > 0) setActiveGroupId(groups[0]!.id);
      return;
    }
    const stillExists = groups.some((g) => g.id === activeGroupId);
    if (!stillExists) setActiveGroupId(fallback);
  }, [me, isAdmin, groupsQuery.data, groups, activeGroupId, setActiveGroupId]);

  const query = useQuery({
    queryKey: ['standings', 'overall', activeGroupId],
    queryFn: () =>
      api.get<StandingsResponse>(
        `/api/standings?groupId=${encodeURIComponent(activeGroupId)}`,
      ),
    refetchInterval: 60_000,
  });

  // Defensive 403 recovery — covers races where the persisted id became
  // stale on the server before the reconcile effect above caught it.
  useEffect(() => {
    if (!query.isError) return;
    const err = query.error;
    if (err instanceof ApiError && err.status === 403 && err.code === 'FORBIDDEN') {
      const fallback = fallbackScopeFor(!!isAdmin, groups.map((g) => g.id));
      if (activeGroupId !== fallback) setActiveGroupId(fallback);
    }
  }, [query.isError, query.error, activeGroupId, isAdmin, groups, setActiveGroupId]);

  const liveQuery = useQuery({
    queryKey: ['matches', 'live'],
    queryFn: () => api.get<LiveMatchesResponse>('/api/matches/live'),
    refetchInterval: 20_000,
  });
  const liveMatches = liveQuery.data?.matches ?? [];

  const rows = query.data?.standings ?? [];
  const top = rows.slice(0, 8);
  const totalUsers = query.data?.total ?? rows.length;
  const matchesScored = rows[0]?.matchesScored ?? 0;
  const exactCountTotal = rows.reduce((n, r) => n + r.exactCount, 0);

  const myRow = me ? (rows.find((r) => r.userId === me.id) ?? null) : null;
  const myPosition = myRow?.position ?? null;

  return (
    <aside className="rail">
      {liveMatches.length > 0 ? (
        <>
          <h3>
            <span className="bar" />
            Live Games
          </h3>
          <div className="live-list">
            {liveMatches.map((m) => (
              <LiveGameRow key={m.id} m={m} />
            ))}
          </div>
        </>
      ) : null}

      <h3>
        <span className="bar" />
        Standings
      </h3>

      {query.isLoading ? (
        <div style={{ padding: '16px 4px', display: 'flex', justifyContent: 'center' }}>
          <Spinner size="sm" />
        </div>
      ) : query.isError ? (
        <p
          style={{
            padding: '16px 4px',
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--danger)',
          }}
        >
          Couldn't load standings.
        </p>
      ) : top.length === 0 ? (
        <p
          style={{
            padding: '16px 4px',
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--fg-muted)',
          }}
        >
          No scored predictions yet.
        </p>
      ) : (
        <div className="lb">
          {top.map((row) => {
            const position = row.position;
            const isMe = me && row.userId === me.id;
            return (
              <div
                key={row.userId}
                className={cn(
                  'lb-row',
                  isMe && 'you',
                  position <= 3 && `r${position}`,
                )}
              >
                <div className="rank">{position}</div>
                <CrownedFlag
                  crowned={position === 1}
                  url={row.favoriteTeamFlagUrl}
                  name={row.displayName}
                  size="xs"
                />
                <div className="who">
                  <div className="n">
                    {row.displayName}
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        marginLeft: 4,
                        verticalAlign: 'middle',
                      }}
                    >
                      <TierBadge tier={row.paymentTier} size="xs" />
                    </span>
                    {isMe ? (
                      <span
                        className="mono"
                        style={{
                          marginLeft: 8,
                          fontSize: 9,
                          background:
                            'color-mix(in oklab, var(--team-accent) 18%, transparent)',
                          color: 'var(--team-accent)',
                          padding: '2px 6px',
                          borderRadius: 999,
                          letterSpacing: '0.12em',
                          fontWeight: 700,
                        }}
                      >
                        YOU
                      </span>
                    ) : null}
                  </div>
                  <div className="m">
                    {row.matchesScored} · {row.scoredCount} scored ({row.exactCount} exact)
                  </div>
                </div>
                <div className="pts tabular">
                  <span
                    style={
                      (row.livePoints ?? 0) > 0
                        ? { color: 'var(--turf)' }
                        : undefined
                    }
                  >
                    {row.totalPoints + (row.livePoints ?? 0)}
                  </span>
                  {(row.livePoints ?? 0) > 0 ? (
                    <span className="live">+{row.livePoints}</span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Link
        to="/standings"
        className="link"
        style={{
          display: 'block',
          textAlign: 'center',
          padding: '8px 0 24px',
          fontSize: 11,
          color: 'var(--fg-muted)',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        View full table →
      </Link>

      <h3>
        <span className="bar" />
        Tournament
      </h3>
      <div className="rail-stats">
        <div className="stat">
          <div className="k">Matches</div>
          <div className="v">
            {matchesScored}
            <span className="unit">matches</span>
          </div>
        </div>
        <div className="stat">
          <div className="k">Exacts</div>
          <div className="v">{exactCountTotal}</div>
        </div>
        {myPosition !== null && myRow ? (
          <>
            <div className="stat">
              <div className="k">Your rank</div>
              <div className="v">
                #{myPosition}
                <span className="unit">of {totalUsers}</span>
              </div>
            </div>
            <div className="stat">
              <div className="k">Your pts</div>
              <div
                className="v"
                style={
                  (myRow.livePoints ?? 0) > 0
                    ? { color: 'var(--turf)' }
                    : undefined
                }
              >
                {myRow.totalPoints + (myRow.livePoints ?? 0)}
                {(myRow.livePoints ?? 0) > 0 ? (
                  <span className="unit">+{myRow.livePoints}</span>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </aside>
  );
}

function LiveGameRow({ m }: { m: MatchPublic }) {
  const result = m.result;
  const main = result?.extraTime ?? result?.fullTime ?? { team1: 0, team2: 0 };
  const pens = result?.penalties ?? null;
  const paused = m.status === 'paused';

  return (
    <Link to={`/matches/${m.id}`} className="live-row">
      <div className="lr-top">
        {paused ? (
          <span className="lr-status paused">{matchPausedLabel(m.minute)}</span>
        ) : (
          <span className="lr-status">
            <span className="live-dot" />
            LIVE
            {typeof m.minute === 'number' ? (
              <span className="lr-minute">{liveMinuteLabel(m.minute, m.addedTime)}</span>
            ) : null}
          </span>
        )}
        <span className="lr-stage">
          {m.group ? `Grp ${m.group}` : m.stage.toUpperCase()}
        </span>
      </div>
      <div className="lr-teams">
        <div className="lr-team">
          <TeamFlag
            code={m.team1?.fifaCode ?? null}
            url={m.team1?.flagUrl ?? null}
            size="xs"
          />
          <span className="lr-code">{m.team1?.fifaCode ?? '—'}</span>
        </div>
        <div className="lr-score tabular">
          {pens ? <span className="lr-pens">({pens.team1})</span> : null}
          {main.team1}–{main.team2}
          {pens ? <span className="lr-pens">({pens.team2})</span> : null}
        </div>
        <div className="lr-team right">
          <span className="lr-code">{m.team2?.fifaCode ?? '—'}</span>
          <TeamFlag
            code={m.team2?.fifaCode ?? null}
            url={m.team2?.flagUrl ?? null}
            size="xs"
          />
        </div>
      </div>
    </Link>
  );
}
